import axios from "axios";
import { useEffect, useState } from "react";
import { baseURL } from "../Constants";

const url = `${baseURL}/Minigame/real-photo-ids`;

export const CACHE_KEY = 'real_photo_ids_cache_v1';
export const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

interface CachedIds {
    data: string[];
    timestamp: number;
}

/**
 * The steamIds the CDN actually has a photo for.
 *
 * Roughly half of players have no real photo — the league CDN answers with a black silhouette
 * placeholder instead — so anything rendering a list of avatars needs to know which is which
 * *before* it renders, or it shows a wall of silhouettes. The browser cannot tell: dpl.gg is
 * cross-origin with no CORS, so the pixels are unreadable and the canvas is tainted.
 *
 * Asking the backend per player (`/Minigame/real-photo/{steamId}`) is not an option at list scale —
 * only its verdict is cached, so each request re-fetches and re-encodes the image. Hence this: one
 * list, fetched once per page-load at worst, and the photos themselves still come straight from the
 * CDN through {@link PlayerImage}.
 */
export const getRealPhotoIds = async (): Promise<string[]> => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const { data, timestamp }: CachedIds = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION_MS) {
                return data;
            }
        }
    } catch (e) {
        console.warn('Failed to parse real-photo cache:', e);
    }

    try {
        const response = await axios.get<string[]>(url);
        const ids = response.data ?? [];

        localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: ids,
            timestamp: Date.now()
        }));

        return ids;
    } catch (error) {
        console.error('Error fetching real-photo ids:', error);

        // Stale cache beats no data — a slightly outdated list only misjudges the handful of
        // players whose photo changed since.
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { data }: CachedIds = JSON.parse(cached);
                return data;
            }
        } catch (e) {
            // Ignore
        }

        return [];
    }
};

// ---------------------------------------------------------------------------
// Shared page-wide photo-availability state. Same shape as BadgeService: localStorage is read
// synchronously at module load so a warm cache never flickers, and a cold cache costs the whole
// page exactly one request.
// ---------------------------------------------------------------------------
let moduleIds: Set<string> | null = null;

try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
        const { data, timestamp }: CachedIds = JSON.parse(raw);
        if (Date.now() - timestamp < CACHE_DURATION_MS) {
            moduleIds = new Set(data);
        }
    }
} catch {
    // Ignore parse errors
}

let idsPromise: Promise<Set<string>> | null = null;
if (!moduleIds) {
    idsPromise = getRealPhotoIds().then((ids) => {
        moduleIds = new Set(ids);
        return moduleIds;
    });
}

/**
 * Which players have a real photo. Resolves synchronously on a warm cache, otherwise null until the
 * one shared background fetch lands — render the fallback until then.
 */
export const useRealPhotoIds = (): Set<string> | null => {
    const [ids, setIds] = useState<Set<string> | null>(moduleIds);

    useEffect(() => {
        if (!ids && idsPromise) {
            idsPromise.then(setIds);
        }
    }, [ids]);

    return ids;
};
